// src/pages/finance-portal/invoice-disputes/InvoiceDisputes.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, IconButton, Alert, CircularProgress, Skeleton, Tooltip, Tabs, Tab,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    OPEN: { bg: '#dbeafe', color: '#1e40af' },
    UNDER_REVIEW: { bg: '#fef3c7', color: '#92400e' },
    RESOLVED: { bg: '#dcfce7', color: '#166534' },
    REJECTED: { bg: '#fecaca', color: '#991b1b' },
};

const STATUS_TABS = [
    { label: 'All', value: '' },
    { label: 'Open', value: 'OPEN' },
    { label: 'Under Review', value: 'UNDER_REVIEW' },
    { label: 'Resolved', value: 'RESOLVED' },
    { label: 'Rejected', value: 'REJECTED' },
];

function formatDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function InvoiceDisputes() {
    const [disputes, setDisputes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [tabValue, setTabValue] = useState(0);

    // Create dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ invoice_id: '', reason: '' });
    const [creating, setCreating] = useState(false);

    // Resolve/Reject dialog
    const [resolveDialog, setResolveDialog] = useState({ open: false, dispute: null, action: '' });
    const [resolutionNotes, setResolutionNotes] = useState('');
    const [resolving, setResolving] = useState(false);

    const statusFilter = STATUS_TABS[tabValue]?.value || '';

    const fetchDisputes = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            const res = await axiosInstance.get('/api/finance/disputes', { params });
            if (res.data.success) {
                setDisputes(res.data.data || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load disputes');
        } finally {
            setIsLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { fetchDisputes(); }, [fetchDisputes]);

    const handleCreate = async () => {
        setCreating(true);
        try {
            await axiosInstance.post('/api/finance/disputes', {
                invoice_id: parseInt(createForm.invoice_id),
                reason: createForm.reason,
            });
            setSuccess('Dispute created');
            setCreateOpen(false);
            setCreateForm({ invoice_id: '', reason: '' });
            fetchDisputes();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create dispute');
        } finally {
            setCreating(false);
        }
    };

    const handleResolve = async () => {
        if (!resolveDialog.dispute) return;
        setResolving(true);
        try {
            const endpoint = resolveDialog.action === 'resolve'
                ? `/api/finance/disputes/${resolveDialog.dispute.id}/resolve`
                : `/api/finance/disputes/${resolveDialog.dispute.id}/reject`;
            await axiosInstance.post(endpoint, { resolution_notes: resolutionNotes });
            setSuccess(`Dispute ${resolveDialog.action === 'resolve' ? 'resolved' : 'rejected'}`);
            setResolveDialog({ open: false, dispute: null, action: '' });
            setResolutionNotes('');
            fetchDisputes();
        } catch (err) {
            setError(err.response?.data?.message || `Failed to ${resolveDialog.action} dispute`);
        } finally {
            setResolving(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <GavelIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Invoice Disputes</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Manage invoice dispute cases</Typography>
                        </Box>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, color: '#fff', boxShadow: 'none' }}
                    >
                        New Dispute
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Status Tabs */}
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2,
                    '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem', minHeight: 40 },
                    '& .Mui-selected': { color: `${ACCENT} !important` },
                    '& .MuiTabs-indicator': { bgcolor: ACCENT },
                }}>
                    {STATUS_TABS.map(t => <Tab key={t.value} label={t.label} />)}
                </Tabs>

                {/* Disputes Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['ID', 'Invoice', 'Reason', 'Status', 'Created', 'Resolution', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3].map(i => <TableRow key={i}><TableCell colSpan={7}><Skeleton height={36} /></TableCell></TableRow>)
                                ) : disputes.length === 0 ? (
                                    <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                        <GavelIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No disputes found</Typography>
                                    </TableCell></TableRow>
                                ) : disputes.map(d => {
                                    const statusStyle = STATUS_COLORS[d.status] || { bg: '#f1f5f9', color: '#64748b' };
                                    return (
                                        <TableRow key={d.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>#{d.id}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {d.invoice_number || `INV-${d.invoice_id}` || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {d.reason}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={d.status?.replace(/_/g, ' ')} size="small"
                                                    sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{formatDate(d.created_at)}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {d.resolution_notes || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {(d.status === 'OPEN' || d.status === 'UNDER_REVIEW') && (
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Tooltip title="Resolve">
                                                            <IconButton size="small" onClick={() => { setResolveDialog({ open: true, dispute: d, action: 'resolve' }); setResolutionNotes(''); }}
                                                                sx={{ color: '#94a3b8', '&:hover': { color: '#22c55e' } }}
                                                            >
                                                                <CheckCircleIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Reject">
                                                            <IconButton size="small" onClick={() => { setResolveDialog({ open: true, dispute: d, action: 'reject' }); setResolutionNotes(''); }}
                                                                sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                                                            >
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>

            {/* Create Dispute Dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>New Dispute</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    <TextField label="Invoice ID" value={createForm.invoice_id}
                        onChange={e => setCreateForm(prev => ({ ...prev, invoice_id: e.target.value }))}
                        fullWidth size="small" type="number"
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <TextField label="Reason" value={createForm.reason}
                        onChange={e => setCreateForm(prev => ({ ...prev, reason: e.target.value }))}
                        fullWidth size="small" multiline rows={3}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setCreateOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreate}
                        disabled={creating || !createForm.invoice_id || !createForm.reason}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {creating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Submit Dispute'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Resolve/Reject Dialog */}
            <Dialog open={resolveDialog.open} onClose={() => setResolveDialog({ open: false, dispute: null, action: '' })} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                    {resolveDialog.action === 'resolve' ? 'Resolve Dispute' : 'Reject Dispute'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    {resolveDialog.dispute && (
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                            Dispute #{resolveDialog.dispute.id}: {resolveDialog.dispute.reason}
                        </Typography>
                    )}
                    <TextField label="Resolution Notes" value={resolutionNotes}
                        onChange={e => setResolutionNotes(e.target.value)}
                        fullWidth size="small" multiline rows={3}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setResolveDialog({ open: false, dispute: null, action: '' })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleResolve} disabled={resolving || !resolutionNotes}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: resolveDialog.action === 'resolve' ? ACCENT : '#ef4444',
                            '&:hover': { bgcolor: resolveDialog.action === 'resolve' ? '#16a34a' : '#dc2626' },
                        }}
                    >
                        {resolving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : resolveDialog.action === 'resolve' ? 'Resolve' : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
